import React, { useState } from 'react';
import {
  withStyles,
  Modal,
} from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  setTractorValues,
  deleteTractorItem,
  updateTractorList,
  restoreOriginalTractor,
  createTractorItem,
  searchTractorList,
} from '~/store/dailyparts/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate, formatTime, newMomentDate, parseDate, parseDateTimeToTimestamp, subtractDaysFromNow,
} from '~/utils/moment';
import EditHistoryButton from '~/components/EditHistoryButton';
import usePlatformConfiguration, { usePlatformConfigurationUserAccessLevel } from '~/services/platformConfiguration';
import { usePagination } from '../hooks';

const TRACTOR = 13;

const TractorList = () => {
  const { t: translate } = useTranslation();

  const filters = useSelector(state => state.dailyParts.filters);
  const tractorList = useSelector(state => state.dailyParts.tractorList);
  const turns = useSelector(state => state.manager.turns);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups
    .filter(e => e.id_equipament === TRACTOR), shallowEqual);
  const equipments = useSelector(state => state.manager.equipments
    .filter(e => e.id_equip === TRACTOR), shallowEqual);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators.filter(o => o.name), shallowEqual);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const materials = useSelector(state => state.manager.materials);
  const originalTractorList = useSelector(state => state.dailyParts.originalTractorList);
  const tractorPagination = useSelector(
    state => state.dailyParts.pagination.tractorPagination,
  );

  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const closures = useSelector(state => state.manager.closures);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const dispatch = useDispatch();

  const isDateEditable = (rowData, key) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (enableClosure && closures?.length > 0) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig
        : lockEditLastClosure.add(1, 'day');
    }

    if (rowData[key] < userLimitEdit) {
      return { isValid: false, helperText: translate('validation:InvalidDate') };
    }

    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || currentDatetime.format('DD/MM/YYYY');
    const stTime = rowData.start_time || currentDatetime.format('HH:mm:ss');
    const endDate = rowData.end_date || currentDatetime.format('DD/MM/YYYY');
    const endTime = rowData.end_time || currentDatetime.format('HH:mm:ss');

    const startDateTimestamp = parseDateTimeToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateTimeToTimestamp(endDate, endTime);

    if (key.startsWith('end_')) {
      if (endDateTimestamp < startDateTimestamp) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (endDateTimestamp > currentDatetime.unix() * 1000) {
        return {
          isValid: false,
          helperText: translate('validation:FutureDateCantBeSelected'),
        };
      }
    }

    return { isValid: true };
  };

  const sortElements = list => list
    .sort((i, k) => (i.name < k.name ? 1 : -1))
    .sort(i => i.active * -1);

  const withInactiveLabel = list => list.map(e => ({
    ...e,
    name: e.active ? e.name : `${e.name} - Inativo`,
  }));

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      title: translate('common:CycleInitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'start_date'),
    },
    {
      title: translate('common:CycleInitialTime'),
      field: 'start_time',
      customType: 'time',
    },
    {
      title: translate('common:CycleFinalDate'),
      field: 'end_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'end_date'),
    },
    {
      title: translate('common:CycleFinalTime'),
      field: 'end_time',
      customType: 'time',
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: equipmentsGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        equip_id: '',
      }),
    },
    {
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: equipments,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group) === String(rowData.equip_group_id),
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        operator_id: '',
      }),
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group)
        === String(rowData.operator_group_id),
    },
    {
      title: translate('common:ReturnDate'),
      field: 'return_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'return_date'),
    },
    {
      title: translate('common:ReturnTime'),
      field: 'return_time',
      customType: 'time',
    },
    {
      title: translate('common:ReturnLatitude'),
      field: 'return_latitude',
      customType: 'number',
    },
    {
      title: translate('common:ReturnLongitude'),
      field: 'return_longitude',
      customType: 'number',
    },
    {
      title: translate('common:ReturnAltitude'),
      field: 'return_altitude',
      customType: 'number',
    },
    {
      title: translate('common:ReturnElement'),
      field: 'return_gps_area',
      lookup: withInactiveLabel(sortElements(elements)),
      customType: 'filterLookup',
      customOnChange: () => ({
        return_gps_subarea: '',
      }),
    },
    {
      title: translate('common:ReturnSubElement'),
      field: 'return_gps_subarea',
      lookup: withInactiveLabel(sortElements(subElements)),
      lookupFilter: (lookup, rowData) => String(lookup.id_element)
        === String(rowData.return_gps_area),
      customType: 'filterLookup',
    },
    {
      title: translate('common:ReturnDistance'),
      field: 'dist_return',
      customType: 'number',
    },
    {
      editable: 'never',
      title: translate('common:ReturnDuration'),
      field: 'return_duration',
    },
    {
      title: translate('common:CutDate'),
      field: 'cut_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'cut_date'),
    },
    {
      title: translate('common:CutTime'),
      field: 'cut_time',
      customType: 'time',
    },
    {
      title: translate('common:CutLatitude'),
      field: 'cut_latitude',
      customType: 'number',
    },
    {
      title: translate('common:CutLongitude'),
      field: 'cut_longitude',
      customType: 'number',
    },
    {
      title: translate('common:CutAltitude'),
      field: 'cut_altitude',
      customType: 'number',
    },
    {
      title: translate('common:CutElement'),
      field: 'cut_gps_area',
      lookup: withInactiveLabel(sortElements(elements)),
      customType: 'filterLookup',
      customOnChange: () => ({
        cut_gps_subarea: '',
      }),
    },
    {
      title: translate('common:CutSubElement'),
      field: 'cut_gps_subarea',
      lookup: withInactiveLabel(sortElements(subElements)),
      lookupFilter: (lookup, rowData) => String(lookup.id_element) === String(rowData.cut_gps_area),
      customType: 'filterLookup',
    },
    {
      title: translate('common:Material'),
      field: 'material',
      lookup: materials,
      customType: 'filterLookup',
    },
    {
      title: translate('common:OperationType'),
      field: 'cut_type',
      customType: 'text',
    },
    {
      title: translate('common:Productivity'),
      field: 'prod',
      customType: 'number',
    },
    {
      title: translate('common:CutDistance'),
      field: 'dist_cut',
      customType: 'number',
    },
    {
      title: translate('common:CutDuration'),
      field: 'cut_duration',
      customType: 'number',
    },
    {
      title: translate('common:CodeTime'),
      field: 'code_time',
      customType: 'number',
    },
    {
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns,
      customType: 'filterLookup',
    },
    {
      editable: 'never',
      title: translate('common:CycleDuration'),
      field: 'duration',
      customType: 'number',
    },
  ];

  const customEditHistoryColumns = [
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: equipmentsGroups,
    },
    {
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: equipments,
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
    },
    {
      title: translate('common:ReturnElement'),
      field: 'return_gps_area',
      lookup: elements,
    },
    {
      title: translate('common:ReturnSubElement'),
      field: 'return_gps_subarea',
      lookup: subElements,
    },
    {
      title: translate('common:CutSubElement'),
      field: 'cut_gps_subarea',
      lookup: subElements,
    },
    {
      title: translate('common:Material'),
      field: 'material',
      lookup: materials,
    },
    {
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns,
    },
    {
      title: translate('common:CutElement'),
      field: 'cut_gps_area',
      lookup: elements,
    },
    {
      title: translate('common:CutSubElement'),
      field: 'cut_gps_subarea',
      lookup: subElements,
    },
  ];

  const filterToFormFieldMap = {
    equipmentGroup: 'equip_group_id',
    equipment: 'equip_id',
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
    material: 'material',
    turn: 'turn',
    startOrigin: 'return_gps_area',
    endOrigin: 'cut_gps_area',
    startSubOrigin: 'return_gps_subarea',
    endSubOrigin: 'cut_gps_subarea',
  };

  const convertIfNeeded = (value, key) => {
    if (key === 'codeType' || key === 'codeGroup') {
      return Number(value);
    }
    return value;
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValue = filters[filterKey];

      if (filterValue !== undefined && filterValue !== 0) {
        if (Array.isArray(filterValue) && filterValue.length === 1) {
          const [firstValue] = filterValue;
          if (firstValue !== undefined) {
            acc[formField] = convertIfNeeded(firstValue, filterKey);
          }
        } else {
          acc[formField] = convertIfNeeded(filterValue, filterKey);
        }
      }

      return acc;
    },
    {},
  );

  // Verificar campos obrigatórios para desabilitar o botão de salvar
  const disableSaveAllChangesButton = tractorList?.length ? tractorList
    .filter(row => row.touched)
    .flatMap(row => (
      Object.entries(row).map(([key, value]) => columns.find(col => col.field === key)?.required && value === '')
    ))
    .includes(true) : false;

  const initialFormData = {
    ...filters,
    ...mappedFilterData,
    start_date: formatDate(newMomentDate(), true),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    active: 1,
    end_date: formatTime(newMomentDate(), 'DD/MM/YYYY'),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    equip_group_id: '',
    equip_id: '',
    operator_group_id: '',
    operator_id: '',
    return_date: formatTime(newMomentDate(), 'DD/MM/YYYY'),
    return_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    return_latitude: '',
    return_longitude: '',
    return_altitude: '',
    return_gps_area: '',
    return_gps_subarea: '',
    dist_return: '',
    return_duration: 0,
    cut_date: formatTime(newMomentDate(), 'DD/MM/YYYY'),
    cut_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    cut_latitude: '',
    cut_longitude: '',
    cut_altitude: '',
    cut_gps_area: '',
    cut_gps_subarea: '',
    material: '',
    cut_type: '',
    prod: 5,
    dist_cut: '',
    cut_duration: 0,
    code_time: '',
    turn: '',
    duration: 0,
    idle_time: null,
  };

  const removeUnnecessaryFields = (data) => {
    const result = { ...data };
    const fieldsToRemove = [
      'startDate',
      'endDate',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
      'id_bulldozer',
      'id_bulldozer_group',
      'id_group',
      'id_equip',
      'id_equip_group',
      'id_material',
      'cut_date',
      'return_date',
    ];

    fieldsToRemove.forEach((field) => {
      delete result[field];
    });

    return result;
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'tractor',
    pagination: tractorPagination,
    searchAction: searchTractorList,
  });

  const { page = 0, total = 0, loadingMore = false } = tractorPagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        columns={applyDefaultSort(columns)}
        data={tractorList}
        initialFormData={initialFormData}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          selection: true,
          search: true,
          cacheKey: 'tractor_list',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          disableSaveButton: disableSaveAllChangesButton,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={(newItem) => {
          const createParams = {
            ...newItem,
            ...mappedFilterData,
          };
          dispatch(createTractorItem(createParams));
        }}
        onDiscard={() => dispatch(restoreOriginalTractor())}
        onApply={() => {
          const parsedData = tractorList
            .filter(item => item.update_status)
            .map(removeUnnecessaryFields);

          const actionData = {
            data: parsedData,
            parameters: filters,
            saveLog: true,
            ogData: originalTractorList,
          };
          dispatch(updateTractorList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach(({ ...row }) => {
            dispatch(setTractorValues(row, row));
          });
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deleteTractorItem(e));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="tractor"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

TractorList.propTypes = {};

TractorList.defaultProps = {};

export default withStyles(styles)(TractorList);
